import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { getPointeuseToken } from "./config.services";

// URL de base de l'API (utilise VITE_API_URL en prod, localhost en dev)
const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
});

api.interceptors.request.use((config) => {
  const token = getPointeuseToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Retry léger sur les erreurs réseau (pas de réponse du serveur).
// Limité aux méthodes idempotentes (GET, HEAD, OPTIONS) pour éviter
// les doubles soumissions sur les pointages (POST/PATCH).
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 500;
const IDEMPOTENT_METHODS = new Set(["get", "head", "options"]);

interface RetryableConfig extends AxiosRequestConfig {
  __retryCount?: number;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryableConfig | undefined;

    // Conditions de retry : config présente, méthode idempotente, pas de réponse serveur
    const method = (config?.method || "get").toLowerCase();
    const isNetworkError = !error.response;
    const isIdempotent = IDEMPOTENT_METHODS.has(method);

    if (!config || !isNetworkError || !isIdempotent) {
      return Promise.reject(error);
    }

    config.__retryCount = config.__retryCount ?? 0;
    if (config.__retryCount >= MAX_RETRIES) {
      return Promise.reject(error);
    }

    config.__retryCount += 1;
    await sleep(RETRY_DELAY_MS * config.__retryCount);
    return api.request(config);
  }
);
